import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { Travel, TravelDocument } from 'src/app/models/travel';
import { Profile } from 'src/app/models/profile';
import { TravelService } from 'src/app/services/travel/travel.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { AlertService } from 'src/app/services/alert/alert.service';
import { ExtLinkService } from 'src/app/services/ext-link/ext-link.service';
import { ROUTE_TYPE } from 'src/app/constants';

@Component({
  selector: 'modal-travel-documents',
  templateUrl: './modal-travel-documents.component.html',
  styleUrls: ['./modal-travel-documents.component.scss'],
})

export class ModalTravelDocumentsComponent implements OnInit {

  @Input() travel: Travel = {} as Travel;
  @Input() profile: Profile;
  @ViewChild('docFile', { static: true }) private docFileInput!: ElementRef;

  documents: TravelDocument[] = [];
  isLoading = true;

  constructor(
    private modal: ModalController,
    private alertController: AlertController,
    private travelService: TravelService,
    private auth: AuthService,
    private spinner: SpinnerService,
    private alert: AlertService,
    private extLink: ExtLinkService,
  ) {}

  async ngOnInit() {
    await this.loadDocuments();
  }

  async loadDocuments() {
    try {
      this.isLoading = true;
      const token = await this.auth.getToken();
      this.documents = await this.travelService.getDocuments(token, this.travel.id);
      this.isLoading = false;
    } catch (err) {
      this.isLoading = false;
    }
  }

  isMyDoc(doc: TravelDocument): boolean {
    return String(doc.creator.id) === String(this.profile && this.profile.id);
  }

  getCreatorLabel(doc: TravelDocument): string {
    const creatorId = String(doc.creator.id);
    const ownerId = String((this.travel && this.travel.route && this.travel.route.owner && this.travel.route.owner.id) || '');
    const actorId = String((this.travel && this.travel.actor && this.travel.actor.id) || '');
    const routeType = this.travel && this.travel.route && this.travel.route.type;

    let role = '';
    if (creatorId === ownerId) {
      role = routeType === ROUTE_TYPE.send ? 'Mittente' : 'Trasportatore';
    } else if (creatorId === actorId) {
      role = routeType === ROUTE_TYPE.send ? 'Trasportatore' : 'Mittente';
    }

    const name = doc.creator.firstName + ' ' + doc.creator.lastName;
    const isMe = creatorId === String((this.profile && this.profile.id) || '');
    return name + (role ? ' · ' + role : '') + (isMe ? ' (tu)' : '');
  }

  getDocTypeLabel(doc: TravelDocument): string {
    const map: { [key: string]: string } = {
      generic: 'Documento generico',
      cmr: 'CMR (Lettera di vettura internazionale)',
      bill_of_lading: 'Polizza di carico',
      air_waybill: 'Lettera di trasporto aereo',
      packing_list: 'Lista imballaggi',
      commercial_invoice: 'Fattura commerciale',
      customs_declaration: 'Dichiarazione doganale',
      delivery_note: 'Bolla di consegna'
    };
    return doc.documentType ? (map[doc.documentType] || doc.documentType) : '';
  }

  openDocument(doc: TravelDocument) {
    this.extLink.openBrowser(doc.url || '');
  }

  async confirmDelete(event: Event, doc: TravelDocument) {
    event.stopPropagation();
    const alert = await this.alertController.create({
      header: 'Elimina documento',
      message: 'Vuoi eliminare "' + doc.filename + '"?',
      buttons: [
        { text: 'Annulla', role: 'cancel' },
        {
          text: 'Elimina',
          role: 'destructive',
          handler: () => { this.doDelete(doc); },
        },
      ],
    });
    await alert.present();
  }

  private async doDelete(doc: TravelDocument) {
    try {
      await this.spinner.show();
      const token = await this.auth.getToken();
      await this.travelService.deleteDocument(token, this.travel.id, doc.id);
      await this.spinner.dismiss();
      await this.alert.toastSuccess();
      await this.loadDocuments();
    } catch (err) {
      await this.spinner.dismissWithAlertError(err);
    }
  }

  addDocument() {
    this.docFileInput.nativeElement.click();
  }

  async onFileChange(files: FileList) {
    if (!files || !files[0]) { return; }
    const file = files[0];
    this.docFileInput.nativeElement.value = '';

    const documentType = await this.selectDocumentType();

    try {
      const buffer = await this.fileToBase64(file);
      await this.spinner.show();
      const token = await this.auth.getToken();
      await this.travelService.uploadDocument(token, this.travel.id, {
        buffer,
        mimeType: file.type,
        documentType: documentType || undefined,
      });
      await this.spinner.dismiss();
      await this.alert.toastSuccess();
      await this.loadDocuments();
    } catch (err) {
      await this.spinner.dismissWithAlertError(err);
    }
  }

  private selectDocumentType(): Promise<string | null> {
    const types = [
      { value: 'generic', label: 'Documento generico' },
      { value: 'cmr', label: 'CMR' },
      { value: 'bill_of_lading', label: 'Polizza di carico' },
      { value: 'air_waybill', label: 'Lettera di trasporto aereo' },
      { value: 'packing_list', label: 'Lista imballaggi' },
      { value: 'commercial_invoice', label: 'Fattura commerciale' },
      { value: 'customs_declaration', label: 'Dichiarazione doganale' },
      { value: 'delivery_note', label: 'Bolla di consegna' },
    ];

    return new Promise(async resolve => {
      const alert = await this.alertController.create({
        header: 'Tipo di documento',
        inputs: types.map(t => ({
          type: 'radio',
          label: t.label,
          value: t.value,
        })) as any,
        buttons: [
          { text: 'Salta', role: 'cancel', handler: () => resolve(null) },
          { text: 'Conferma', handler: (val) => resolve(val) },
        ],
      });
      await alert.present();
    });
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
    });
  }

  close() {
    this.modal.dismiss();
  }
}
